import React, { useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import getCashRegister from '../../../gql/getCashRegister'
import updateEuroRateMutation from '../../../gql/updateCashRegisterEuroRate'
import get from 'lodash/get'
import set from 'lodash/set'
import { minMaxVal } from 'helpers/validators'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { FormRow, FormCol } from 'style/styled'
import { Form, Text, Checkbox, Number as NumberInput, Select, DateTimePicker } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { listProducts } from 'sections/product/gql/query'
import { StyledFormRowWithAdditionalLabel } from '../styled'

const CashFlow = ({ formData, itemId, data, updateItem }) => {
	const [t] = useTranslation()
	const [updateCashRegisterEuroRate, { loading: loadingRate }] = useMutation(updateEuroRateMutation)
	const apiRef = useRef()

	const submitCredits = vals => {
		const submitVals = { ...vals }
		const creditCards = get(vals, 'parameters.creditCards', {})
		Object.keys(creditCards).forEach(field => {
			if(Array.isArray(creditCards[field])) {
				creditCards[field] = creditCards[field].reduce((outArr, { value }, i) => {
					if(typeof value !== 'undefined') {
						outArr.push({ value, position: i })
					}
					return outArr
				}, [])
			}
		})
		set(submitVals, 'parameters.creditCards', creditCards)
		updateItem(submitVals)
	}

	const submitSales = vals => {
		const submitDiscountVals = { ...vals }
		const discountForm = get(vals, 'parameters.discountForm', {})
		set(submitDiscountVals, 'parameters.discountForm', discountForm)
		updateItem(submitDiscountVals)
	}

	const submitForeignCurrency = vals => {
		const submitCurrencyValues = { ...vals }
		updateItem(submitCurrencyValues)
	}

	const submitLoyaltyCard = vals => {
		const submitLoyaltyCardValues = { ...vals }
		updateItem(submitLoyaltyCardValues)
	}

	const submitTips = vals => {
		const submitTipsValues = { ...vals }
		updateItem(submitTipsValues)
	}

	const useCurrencyMutation = () => {
		// actual value inside additionPercet - *getState: note this will only return the state as it existed when the function was called.
		const additionPercent = apiRef.current.getState().values.parameters?.foreignCurrency?.euro?.additionPercent

		updateCashRegisterEuroRate({
			variables: {
				cashRegisterId: itemId,
				additionPercent: additionPercent
			},
			update: (cache, { data: mutateData }) => {
				const cacheData = cache.readQuery({
					query: getCashRegister, variables: { id: itemId }
				})
				const currencyFields = mutateData?.cashAssist?.updateCashRegisterEuroRate || {}
				const { ...fields } = currencyFields
				const newDate = { ...cacheData }
				const pathToEuro = ['cashAssist', 'getCashRegister', 'parameters', 'foreignCurrency', 'euro']
				const cachedEuro = get(cacheData, pathToEuro) || {}
				set(newDate, pathToEuro, { ...cachedEuro, ...fields })
				apiRef.current.setValue('parameters.foreignCurrency.euro.exchangeRateIsUsed', fields?.exchangeRateIsUsed)
				cache.writeQuery({
					query: getCashRegister,
					data: newDate
				})
			}
		})
	}
	const renderCreditCardFields = () => {
		const col1 = [], col2 = [], col3 = []
		for(let i = 0; i < 10; i++) {
			col1.push(
				<Text
					key={i}
					field={`parameters.creditCards.clientButtonName[${i}].value`}
					label={`${t('common.fields.name')} ${i + 1}`}
				/>
			)
			col2.push(
				<div key={i}>
					<NumberInput
						field={`parameters.creditCards.startPiecemeal[${i}].value`}
						label={t('options_section.fields.from_amount')}
						precision={2}
						shrink
						withControls={false}
						inline
					/>
					<Checkbox
						field={`parameters.creditCards.isPiecemeal[${i}].value`}
						label={t('options_section.fields.variable')}
						inline
					/>
				</div>
			)
			col3.push(
				<NumberInput
					key={i}
					field={`parameters.creditCards.receiptCopies[${i}].value`}
					label={t('options_section.fields.receipt_copy')}
					shrink
				/>
			)
		}
		return(
			<FormRow>
				<FormCol xs>{col1}</FormCol>
				<FormCol xs autoWidth>{col2}</FormCol>
				<FormCol xs autoWidth>{col3}</FormCol>
			</FormRow>
		)
	}

	const renderSaleFields = () => {
		const col1 = [], col2 = []
		for(let i = 1; i <= 5; i++) {
			col1.push(
				<Text
					key={i}
					field={`parameters.discountForm.buttonName${i}`}
					label={`${t('common.fields.name')} ${i - 1 + 1}`}
				/>
			)
			col2.push(
				<NumberInput
					key={i}
					field={`parameters.discountForm.button${i}`}
					label={`${t('common.fields.discount')}, %`}
					precision={2}
					shrink
					withControls={false}
					format='percent'
				/>
			)
		}

		return(
			<FormRow>
				<FormCol xs>{col1}</FormCol>
				<FormCol xs autoWidth>{col2}</FormCol>
			</FormRow>
		)
	}
	const [errorState, setErrorState] = useState(false)
	//Here we check if the date is correct from the server.
	//If the date is incorrect -> we return "null". If the date is correct -> we return it.
	const dateForEuroCurrency = data?.parameters?.foreignCurrency?.euro?.exchangeDateRefresh
	const correctDate = new Date(dateForEuroCurrency)
	const isValidDate = (date) => {
	 return date instanceof Date && !isNaN(date)
	}
	const memoDateComputation = useMemo(() => isValidDate(correctDate), [correctDate])

	const renderForeignСurrencies = () => {
		return (
			<>
				<FormRow>
					<p style={{ margin: '0 0 7px 20px' }}>{t('options_section.fields.currency_title')}
					</p>
				</FormRow>
				<FormRow>
					<FormCol sm>
						<DateTimePicker
							value={memoDateComputation ? correctDate : null}
							picker='date'
							noForm
							label={t('options_section.fields.currency_rate_date')}
							disabled
							inline
						/>
						<NumberInput
							value={data?.parameters?.foreignCurrency?.euro?.exchangeRate}
							label={t('options_section.fields.currency_rate')}
							noForm
							disabled
							shrink
							withControls={false}
							inline />
						<NumberInput
							field='parameters.foreignCurrency.euro.exchangeRateIsUsed'
							label={t('options_section.fields.currency_exchange_rate_in_use')}
							precision={2}
							shrink
							withControls={false}
							inline />
						<NumberInput
							{...minMaxVal({ min: -100, max: 100, error: `${t('validators.value_between', { minVal: -100, maxVal: 100 })}`, setErrorState })}
							label={t('options_section.fields.currency_addition_percent')}
							field='parameters.foreignCurrency.euro.additionPercent'
							precision={2}
							withControls={false}
							shrink
							inline
						/>
						<Select
							field={`parameters.foreignCurrency.euro.roundTo`}
							label={t('options_section.fields.currency_round_to')}
							shrink
							options={[
								{ value: 'Cent1', label: 0.01 },
								{ value: 'Cent5', label: 0.05 },
								{ value: 'Cent10', label: 0.10 }
							]}
						/>
						<Button
							disabled={errorState}
							status={errorState ? 'inactive' : 'primary'}
							onClick={useCurrencyMutation}
							loading={loadingRate}
							text={t('options_section.strings.currency_update_new_course')}
							inline />
						<Divider />
					</FormCol>
					<FormCol md>
						<Select
							field='parameters.foreignCurrency.euro.changeTo'
							label={t('options_section.fields.currency_payment_system')}
							options={[
								{ value: 'CHF', label: t('options_section.fields.currency_option_standard') },
								{ value: 'CHF_EUR', label: t('options_section.fields.currency_option_or_euro') },
								{ value: 'EUR', label: t('options_section.fields.currency_option_change_euro') }
							]}
						/>
						<Checkbox
							field={`parameters.foreignCurrency.euro.isActive`}
							label={`${t('options_section.fields.currency_euro_active')}`} />
						<Checkbox
							field={`parameters.foreignCurrency.euro.isDefinedMannualy`}
							label={`${t('options_section.fields.currency_defined_mannualy')}`} />
						<Checkbox
							field={`parameters.foreignCurrency.euro.isEuroCoinsNotShown`}
							label={`${t('options_section.fields.currency_coins_not_show')}`} />
						<Checkbox
							field={`parameters.foreignCurrency.euro.is200EuroNotShown`}
							label={`${t('options_section.fields.currency_two_hundred_not_show')}`} />
						<Checkbox field={`parameters.foreignCurrency.euro.isShowTotalEuroOnCustomerDisplay`}
							label={`${t('options_section.fields.currency_show_total_display')}`} />
					</FormCol>
				</FormRow>
			</>
		)
	}

	const renderLoyaltyCard = () => {
		return (
			<StyledFormRowWithAdditionalLabel>
				<Select
					field='parameters.itemLoyaltyCard'
					label={t('options_section.fields.change_item')}
					remoteOptions={{
						query: listProducts,
						variables: {
							filter: {
								isActive: true,
								includeWawiProducts: true,
								includeCashassistProducts: true,
								checkOutCommandId: 230
							}
						},
						valueField: 'id', labelField: 'name'
					}}
					valueField='id'
					labelField='name'
					isClearable
					style={{ minWidth: '200px' }}
				/>
				<span>{t('options_section.strings.with_this_the_change_can_be_booked_on_the_customer_card_in_payment_mode')}</span>
			</StyledFormRowWithAdditionalLabel>
		)
	}

	const renderTips = () => {
		return (
			<FormRow>
				<FormCol xs>
					<Checkbox
						field='parameters.activateTipFunction'
						label={t('options_section.fields.activate_tip_function')}
					/>
				</FormCol>
			</FormRow>
		)
	}
	const creditOptions = data?.parameters?.creditCards?.clientButtonName.map(({ position, value }) => ({
		label: value,
		value: position + 1
	})) || []

	return(
		<ExpandableBlocks nested>
			<ContentBlock label={t('options_section.strings.credit_cards')} startExpanded>
				<Form
					initialValues={formData}
					onSubmit={submitCredits}
					smartSubmit
				>
					{renderCreditCardFields()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.discounts')}>
				<Form
					initialValues={formData}
					onSubmit={submitSales}
					smartSubmit
				>
					<Checkbox
						field='parameters.isDiscountFormDisabled'
						label={t('options_section.fields.discount_default_disabled')}
					/>
					{renderSaleFields()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('closing_section.strings.bill_filter_foreign_currency')}>
				<Form
					apiRef={apiRef}
					initialValues={formData}
					onSubmit={submitForeignCurrency}
					smartSubmit
				>
					{renderForeignСurrencies()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('loyalty_card_section.caption')}>
				<Form
					initialValues={formData}
					onSubmit={submitLoyaltyCard}
					smartSubmit
				>
					{renderLoyaltyCard()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('statistic_section.strings.tips')}>
				<Form
					initialValues={formData}
					onSubmit={submitTips}
					smartSubmit
				>
					{renderTips()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.external_voucher_systems')}>
				<Form
					initialValues={formData}
					onSubmit={submitTips}
					smartSubmit
				>
					<Checkbox field='parameters.isEgumaActive' label={t('options_section.strings.eguma')} inline/>
					<Select
						field='parameters.egumaCreditId'
						label={t('options_section.fields.eguma_credit')}
						inline
						style={{ width: '300px' }}
						options={creditOptions}
					/>
					<Divider/>
					<Checkbox field='parameters.isGuradoActive' label={t('options_section.strings.gurado')} inline/>
					<Select
						field='parameters.guradoCreditId'
						label={t('options_section.fields.gurado_credit')}
						inline
						style={{ width: '300px' }}
						options={creditOptions}
					/>
				</Form>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default CashFlow
